import { ViewSwitch } from '../ui/ListToolbar';
import { useMemo, useState } from 'react';
import {
  ChevronDown,
  FolderOpen,
  LayoutList,
  KanbanSquare,
  CalendarDays,
  User,
  Tag as TagIcon,
  Check,
  CheckCircle2,
  Plus,
  Search as SearchIcon,
  X,
} from 'lucide-react';
import { FaTrashCan } from '../ui/FaIcons';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Avatar } from '../ui/Avatar';
import { DossierPickerPanel } from '../dossiers/DossierPickerPanel';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { NO_TAG, tagFilterLabel } from '../../lib/tasks/taskTagFilter';
import type { ViewMode } from '../../store/tasksStore';
import type { Dossier } from '../../hooks/queries/useDossiers';
import type { CollaboratorSummary } from '../../services/tasks/taskCollaborationService';
import type { TaskTag } from '../../services/tasks/taskTagService';

interface TasksHeaderProps {
  dossiers: Dossier[];
  activeDossier: Dossier | null;
  onSelectDossier: (id: string | null) => void;
  viewMode: ViewMode;
  onChangeViewMode: (mode: ViewMode) => void;
  onOpenArchives?: () => void;
  /** Liste des utilisateurs filtrables — fournie uniquement aux admins. */
  assigneeFilterOptions?: CollaboratorSummary[];
  assigneeFilter?: string | null;
  onChangeAssigneeFilter?: (id: string | null) => void;
  /** Tags disponibles pour le filtre — tous rôles. */
  tagOptions?: TaskTag[];
  tagFilterIds?: string[];
  onChangeTagFilter?: (ids: string[]) => void;
  searchQuery?: string;
  onChangeSearchQuery?: (q: string) => void;
  /** Toggle « Afficher les terminées » (persisté côté store). */
  showCompleted?: boolean;
  onToggleShowCompleted?: () => void;
  /** Vues cabinet actives. Switcher masqué s’il en reste moins de deux. */
  enabledModes?: ViewMode[];
  /** CTA fiche de création (quand la notation rapide est éteinte). */
  onCreateTask?: () => void;
}

const VIEW_MODES: { mode: ViewMode; label: string; Icon: typeof LayoutList }[] = [
  { mode: 'list', label: 'Liste', Icon: LayoutList },
  { mode: 'board', label: 'Kanban', Icon: KanbanSquare },
  { mode: 'calendar', label: 'Calendrier', Icon: CalendarDays },
];

export function TasksHeader({
  dossiers,
  activeDossier,
  onSelectDossier,
  viewMode,
  onChangeViewMode,
  onOpenArchives,
  assigneeFilterOptions,
  assigneeFilter = null,
  onChangeAssigneeFilter,
  tagOptions,
  tagFilterIds = [],
  onChangeTagFilter,
  searchQuery = '',
  onChangeSearchQuery,
  showCompleted = false,
  onToggleShowCompleted,
  enabledModes,
  onCreateTask,
}: TasksHeaderProps) {
  const { tags: showTags } = useEffectiveTaskFields();
  const visibleModes = VIEW_MODES.filter(
    (v) => !enabledModes || enabledModes.includes(v.mode),
  );
  const showSwitcher = visibleModes.length >= 2;
  const [open, setOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);

  const selectedUser = assigneeFilterOptions?.find((c) => c.id === assigneeFilter) ?? null;

  const tagsById = useMemo(
    () => new Map((tagOptions ?? []).map((t) => [t.id, t])),
    [tagOptions],
  );
  const tagLabel = tagFilterLabel(tagFilterIds, tagsById);
  const tagFilterActive = tagFilterIds.length > 0;

  function toggleTag(id: string) {
    const next = tagFilterIds.includes(id)
      ? tagFilterIds.filter((t) => t !== id)
      : [...tagFilterIds, id];
    onChangeTagFilter?.(next);
  }

  return (
    <div className="flex items-center justify-between gap-4 mb-6">
      <div className="flex items-center gap-2">
      {/* Sélecteur de dossier */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
              activeDossier
                ? 'border-cime-primary bg-cime-primary/5'
                : 'border-cime-border bg-white hover:bg-[#fbfaf7]'
            }`}
          >
            <FolderOpen className="w-4 h-4 text-cime-primary" />
            <div className="text-left">
              <div className="text-sm font-semibold text-cime-text-primary leading-tight">
                {activeDossier?.title ?? 'Tous les dossiers'}
              </div>
              {activeDossier?.number && (
                <div className="text-[11px] text-cime-text-muted leading-tight">
                  {activeDossier.number}
                </div>
              )}
            </div>
            <ChevronDown className="w-4 h-4 text-cime-text-muted" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          data-cime-modal
          className="w-80 max-h-[min(80vh,var(--radix-popover-content-available-height))] overflow-hidden p-0 rounded-lg border-cime-border bg-white shadow-lg"
        >
          <DossierPickerPanel
            dossiers={dossiers}
            onPick={(id) => {
              onSelectDossier(id);
              setOpen(false);
            }}
            header={
              <button
                type="button"
                onClick={() => {
                  onSelectDossier(null);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-[#fbfaf7] ${
                  activeDossier === null ? 'bg-cime-primary/5' : ''
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-cime-primary"
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-cime-text-primary">
                    Tous les dossiers
                  </div>
                  <div className="text-[11px] text-cime-text-muted">
                    Toutes les tâches qui me sont visibles
                  </div>
                </div>
              </button>
            }
          />
        </PopoverContent>
      </Popover>

      {/* Recherche plein-texte */}
      {onChangeSearchQuery && (
        <div className="relative">
          <SearchIcon className="w-4 h-4 text-cime-text-muted absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-tasks-search
            type="text"
            value={searchQuery}
            onChange={(e) => onChangeSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                onChangeSearchQuery('');
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="Rechercher une tâche…"
            className="w-52 pl-8 pr-7 py-2 rounded-lg border border-cime-border bg-white text-sm outline-none placeholder:text-cime-text-muted focus:border-cime-primary"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onChangeSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-cime-text-muted hover:text-cime-text-primary"
              aria-label="Effacer la recherche"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Filtre par utilisateur — admin uniquement */}
      {assigneeFilterOptions && (
        <Popover open={userOpen} onOpenChange={setUserOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
                selectedUser
                  ? 'border-cime-primary bg-cime-primary/5'
                  : 'border-cime-border bg-white hover:bg-[#fbfaf7]'
              }`}
              title="Filtrer par utilisateur"
            >
              <User className="w-4 h-4 text-cime-primary" />
              <span className="text-sm font-medium text-cime-text-primary">
                {selectedUser?.full_name ?? 'Tous les utilisateurs'}
              </span>
              <ChevronDown className="w-4 h-4 text-cime-text-muted" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            data-cime-modal
            className="w-auto min-w-[240px] max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto p-0 rounded-lg border-cime-border bg-white shadow-lg"
          >
            <button
              type="button"
              onClick={() => {
                onChangeAssigneeFilter?.(null);
                setUserOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#fbfaf7] ${
                assigneeFilter === null ? 'bg-cime-primary/5 font-semibold' : ''
              }`}
            >
              Tous les utilisateurs
            </button>
            <div className="border-t border-[#efece3]" aria-hidden />
            {assigneeFilterOptions.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onChangeAssigneeFilter?.(c.id);
                  setUserOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#fbfaf7] ${
                  assigneeFilter === c.id ? 'bg-cime-primary/5 font-medium' : ''
                }`}
              >
                <Avatar name={c.full_name} src={c.avatar_url} size="xs" />
                <span className="truncate">{c.full_name}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}

      {/* Filtre par tags — tous rôles */}
      {showTags && tagOptions && tagOptions.length > 0 && (
        <Popover open={tagOpen} onOpenChange={setTagOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
                tagFilterActive
                  ? 'border-cime-primary bg-cime-primary/5'
                  : 'border-cime-border bg-white hover:bg-[#fbfaf7]'
              }`}
              title="Filtrer par étiquette"
            >
              <TagIcon className="w-4 h-4 text-cime-primary" />
              <span className="text-sm font-medium text-cime-text-primary">
                {tagLabel}
              </span>
              <ChevronDown className="w-4 h-4 text-cime-text-muted" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            data-cime-modal
            className="w-auto min-w-[240px] max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto p-0 rounded-lg border-cime-border bg-white shadow-lg"
          >
            <button
              type="button"
              role="checkbox"
              aria-checked={tagFilterIds.includes(NO_TAG)}
              onClick={() => toggleTag(NO_TAG)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#fbfaf7]"
            >
              <span
                className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                  tagFilterIds.includes(NO_TAG)
                    ? 'bg-cime-primary border-cime-primary'
                    : 'border-cime-border'
                }`}
                aria-hidden
              >
                {tagFilterIds.includes(NO_TAG) && <Check className="w-3 h-3 text-white" />}
              </span>
              <span className="text-cime-text-muted italic">Sans étiquette</span>
            </button>
            <div className="border-t border-[#efece3]" aria-hidden />
            {tagOptions.map((t) => (
              <button
                key={t.id}
                type="button"
                role="checkbox"
                aria-checked={tagFilterIds.includes(t.id)}
                onClick={() => toggleTag(t.id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#fbfaf7]"
              >
                <span
                  className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                    tagFilterIds.includes(t.id)
                      ? 'bg-cime-primary border-cime-primary'
                      : 'border-cime-border'
                  }`}
                  aria-hidden
                >
                  {tagFilterIds.includes(t.id) && <Check className="w-3 h-3 text-white" />}
                </span>
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: t.color }}
                  aria-hidden
                />
                <span className="truncate text-cime-text-primary">{t.name}</span>
              </button>
            ))}
            {tagFilterActive && (
              <>
                <div className="border-t border-[#efece3]" aria-hidden />
                <button
                  type="button"
                  onClick={() => onChangeTagFilter?.([])}
                  className="flex w-full items-center justify-center px-3 py-2 text-sm font-medium text-cime-primary hover:bg-[#fbfaf7]"
                >
                  Effacer
                </button>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}

      </div>

      <div className="flex items-center gap-2">
        {onCreateTask && (
          <button
            type="button"
            onClick={onCreateTask}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-cime-primary text-white hover:bg-[#ea7549] transition"
            aria-label="Nouvelle tâche"
            title="Nouvelle tâche"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
        {/* Bascule des vues, en icônes — masquée s’il n’en reste qu’une */}
        {showSwitcher && (
          <ViewSwitch
            label="Vue des tâches"
            value={viewMode}
            onChange={onChangeViewMode}
            options={visibleModes.map(({ mode, label, Icon }) => ({
              value: mode,
              label,
              icon: <Icon aria-hidden />,
            }))}
          />
        )}

        {/* Toggle tâches terminées */}
        {onToggleShowCompleted && (
          <button
            type="button"
            onClick={onToggleShowCompleted}
            aria-pressed={showCompleted}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition ${
              showCompleted
                ? 'border-cime-primary bg-cime-primary/5 text-cime-primary'
                : 'border-cime-border bg-white text-cime-text-muted hover:bg-[#fbfaf7] hover:text-cime-primary'
            }`}
            title={showCompleted ? 'Masquer les tâches terminées' : 'Afficher les tâches terminées'}
          >
            <CheckCircle2 className="w-4 h-4" />
            <span className="hidden md:inline">Terminées</span>
          </button>
        )}

        {/* Bouton corbeille (tâches supprimées) */}
        {onOpenArchives && (
          <button
            type="button"
            onClick={onOpenArchives}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cime-border bg-white text-sm font-medium text-cime-text-muted hover:bg-[#fbfaf7] hover:text-cime-primary transition"
            title="Voir les tâches supprimées"
          >
            <FaTrashCan className="w-4 h-4" />
            <span className="hidden md:inline">Corbeille</span>
          </button>
        )}
      </div>
    </div>
  );
}
